import { useState, useEffect, useCallback, useMemo } from 'react';
import { format, subMonths, addMonths } from 'date-fns';
import {
  MyShift,
  TimeSlot,
  RecruitmentPeriod,
  convertShiftsToMyShifts,
} from '../../../../domain/myShift';
import {
  fetchShifts,
  updateShifts,
  MyShiftApiError,
} from '../../../../infrastructure/api/myShiftApi';
import { ShiftInput } from '../../../../domain/myShift';
import {
  fetchDailyShiftSlots,
  DayShiftSlots,
  MonthlyDailyShiftSlots,
} from '../../../../infrastructure/api/dailyShiftSlotsApi';
import { useAuth } from '../../../../hooks/useAuth';
import { DaySlotSelection, ConfirmDialogType } from '../types';
import { buildDaySlotSelections } from '../daySlots';

/**
 * 未保存の変更（日単位のドラフト）
 * @description shift.timeSlots はその日の「編集可能スロットの最終状態」（空 = 全取消/全不可）
 */
export interface PendingChange {
  /** 対象日付（YYYY-MM-DD形式） */
  date: string;
  /** その日の最終状態 */
  shift: MyShift;
}

/**
 * useMyShiftフックの戻り値
 */
export interface UseMyShiftReturn {
  // ========== State ==========
  /** カレンダー表示用シフト一覧（サーバー状態＋未保存の変更を合成） */
  shifts: MyShift[];
  /** 募集期間 */
  recruitmentPeriod: RecruitmentPeriod | null;
  /** 募集期間取得エラー */
  recruitmentPeriodError: string | null;
  /** 表示中の月 */
  currentMonth: Date;
  /** ローディング中フラグ */
  isLoading: boolean;
  /** エラーメッセージ */
  error: string | null;
  /** リトライ中フラグ */
  isRetrying: boolean;
  /** 日別シフト枠（募集枠）一覧 */
  dailySlots: DayShiftSlots[];
  /** 月次シフト枠データ（募集期間サマリー含む） */
  monthlyStatus: MonthlyDailyShiftSlots | null;
  /** 月次シフト枠フェーズ（draft/fixed_recruiting/fixed_closed/fixed_confirmed/recruiting/closed/confirmed） */
  monthlyStatusPhase: string | null;

  // ========== Pending Changes State ==========
  /** 未保存の変更一覧 */
  pendingChanges: PendingChange[];
  /** 未保存の変更があるか */
  hasPendingChanges: boolean;
  /** 保存中フラグ */
  isSaving: boolean;

  // ========== Form State ==========
  /** フォーム表示フラグ */
  isFormOpen: boolean;
  /** 選択中の日付 */
  selectedDate: Date | null;
  /** 日別フォームのスロット選択状態 */
  selectedSlots: DaySlotSelection[];
  /** 募集枠に一致しない自分のシフト（「その他の予定」表示用） */
  extraShifts: TimeSlot[];
  /** 現在のフェーズで編集可能か */
  formEditable: boolean;
  /** 送信中フラグ */
  isSubmitting: boolean;

  // ========== Confirm Dialog State ==========
  /** 確認ダイアログの種類 */
  confirmDialogType: ConfirmDialogType;

  // ========== Navigation ==========
  handlePreviousMonth: () => void;
  handleNextMonth: () => void;

  // ========== Form Actions ==========
  openShiftForm: (date: Date, existingShift?: MyShift) => void;
  closeShiftForm: () => void;
  updateSlotSelection: (key: string, selected: boolean) => void;

  // ========== Submit Actions ==========
  handleConfirmClick: () => void;
  confirmLocalSave: () => void;
  cancelConfirm: () => void;

  // ========== Save Actions ==========
  saveAllChanges: () => Promise<void>;

  // ========== Retry ==========
  retry: () => Promise<void>;
}

/**
 * マイシフト画面の状態管理フック
 *
 * 保存モデル: 日単位のドラフト（pendingChanges）を「保存」で1回の
 * 対象日付置換 PUT（target_dates / expected_status）として送信する。
 * target_dates 外の日付・編集不可の行には影響しない。
 */
export const useMyShift = (): UseMyShiftReturn => {
  const { user } = useAuth();
  const doctorId = user?.doctor_id;

  // ========== State ==========
  const [shifts, setShifts] = useState<MyShift[]>([]);
  const [recruitmentPeriod, setRecruitmentPeriod] = useState<RecruitmentPeriod | null>(null);
  const [recruitmentPeriodError, setRecruitmentPeriodError] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [dailySlots, setDailySlots] = useState<DayShiftSlots[]>([]);
  const [monthlyStatus, setMonthlyStatus] = useState<MonthlyDailyShiftSlots | null>(null);
  const [monthlyStatusPhase, setMonthlyStatusPhase] = useState<string | null>(null);

  // ========== Pending Changes State ==========
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // ========== Form State ==========
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<DaySlotSelection[]>([]);
  const [extraShifts, setExtraShifts] = useState<TimeSlot[]>([]);
  const [formEditable, setFormEditable] = useState(false);
  const [isSubmitting] = useState(false);

  // ========== Confirm Dialog State ==========
  const [confirmDialogType, setConfirmDialogType] = useState<ConfirmDialogType>(null);

  // ========== Computed ==========
  const hasPendingChanges = useMemo(() => pendingChanges.length > 0, [pendingChanges]);

  // マージされたシフト一覧（APIから取得したシフト + 未保存の変更）
  // 不可・取消した枠も「保存」を押すまではカレンダーに表示し続ける
  // （日付を開き直せばフォームで取り消せる）。追加した枠は即表示する。
  const mergedShifts = useMemo(() => {
    const shiftsMap = new Map<string, MyShift>();

    shifts.forEach(shift => {
      shiftsMap.set(shift.date, shift);
    });

    pendingChanges.forEach(change => {
      const serverShift = shiftsMap.get(change.date);
      const serverSlots = serverShift?.timeSlots ?? [];
      const serverKeys = new Set(
        serverSlots.map(slot => `${slot.startTime}-${slot.endTime}`)
      );
      const addedSlots = change.shift.timeSlots.filter(
        slot => !serverKeys.has(`${slot.startTime}-${slot.endTime}`)
      );
      const mergedTimeSlots = [...serverSlots, ...addedSlots].sort(
        (a, b) => a.startTime.localeCompare(b.startTime)
      );

      if (mergedTimeSlots.length === 0) {
        shiftsMap.delete(change.date);
      } else {
        shiftsMap.set(change.date, {
          ...(serverShift ?? change.shift),
          timeSlots: mergedTimeSlots,
        });
      }
    });

    return Array.from(shiftsMap.values());
  }, [shifts, pendingChanges]);

  // ========== Fetch Data ==========
  const fetchData = useCallback(
    async (month: Date, onRetrying?: () => void) => {
      if (!doctorId) {
        setError('医師IDが取得できません。再ログインしてください。');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const yearMonth = format(month, 'yyyy-MM');
        const shiftsResponse = await fetchShifts(doctorId, yearMonth, onRetrying);

        if (shiftsResponse.success) {
          const phase = shiftsResponse.meta?.monthly_status ?? null;
          setMonthlyStatusPhase(phase);
          setShifts(convertShiftsToMyShifts(shiftsResponse.data, phase));
        }

        const year = month.getFullYear();
        const monthNum = month.getMonth() + 1;

        try {
          const slotsResponse = await fetchDailyShiftSlots(year, monthNum);
          if (slotsResponse.success && slotsResponse.data) {
            const monthly = slotsResponse.data;
            // 月次ステータスは日別シフト枠APIが返す。シフト一覧APIのmetaには含まれない環境がある。
            setMonthlyStatusPhase(monthly.status);
            if (shiftsResponse.success) {
              setShifts(convertShiftsToMyShifts(shiftsResponse.data, monthly.status));
            }
            setDailySlots(monthly.days ?? []);
            setMonthlyStatus(monthly);

            if (monthly.recruitmentStartAt && monthly.deadlineAt) {
              setRecruitmentPeriod({
                startDate: monthly.recruitmentStartAt,
                endDate: monthly.deadlineAt,
              });
            } else {
              setRecruitmentPeriod(null);
            }
            setRecruitmentPeriodError(null);
          }
        } catch {
          setRecruitmentPeriodError('募集期間の取得に失敗しました');
        }
      } catch (err: unknown) {
        const fetchError = err as Error;
        setError(fetchError.message || 'シフトデータの取得に失敗しました。再読み込みしてください。');
        setRecruitmentPeriodError('取得できませんでした');
      } finally {
        setIsLoading(false);
        setIsRetrying(false);
      }
    },
    [doctorId]
  );

  useEffect(() => {
    fetchData(currentMonth);
  }, [currentMonth, fetchData]);

  // 月変更時に未保存の変更をクリア
  useEffect(() => {
    setPendingChanges([]);
  }, [currentMonth]);

  // ========== Navigation ==========
  const handlePreviousMonth = useCallback(() => {
    setCurrentMonth(prev => subMonths(prev, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setCurrentMonth(prev => addMonths(prev, 1));
  }, []);

  // ========== Form Actions ==========
  const openShiftForm = useCallback(
    (date: Date, existingShift?: MyShift) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dailySlot = (dailySlots ?? []).find(slot => slot.date === dateStr);

      // フォームはサーバー状態から構築する（merged 表示からではなく）。
      // 未保存の不可・取消で枠がフォームから消えず、保存前なら取り消せる
      const serverShift = shifts.find(shift => shift.date === dateStr) ?? existingShift;

      const { slots, extraShifts: extras, editable } = buildDaySlotSelections(
        monthlyStatusPhase,
        serverShift,
        dailySlot,
      );

      // その日の未保存の変更があれば、選択状態だけ上書きして復元する
      const pending = pendingChanges.find(change => change.date === dateStr);
      const selections = pending
        ? slots.map(slot => {
            if (slot.locked) {
              return slot;
            }
            const selected = pending.shift.timeSlots.some(
              ts => `${ts.startTime}-${ts.endTime}` === slot.key
            );
            return { ...slot, selected };
          })
        : slots;

      setSelectedDate(date);
      setSelectedSlots(selections);
      setExtraShifts(extras);
      setFormEditable(editable);
      setIsFormOpen(true);
    },
    [dailySlots, monthlyStatusPhase, shifts, pendingChanges]
  );

  const closeShiftForm = useCallback(() => {
    setIsFormOpen(false);
    setSelectedDate(null);
    setSelectedSlots([]);
    setExtraShifts([]);
    setFormEditable(false);
    setConfirmDialogType(null);
  }, []);

  const updateSlotSelection = useCallback((key: string, selected: boolean) => {
    setSelectedSlots(prev =>
      prev.map(slot =>
        slot.key === key && !slot.locked ? { ...slot, selected } : slot
      )
    );
  }, []);

  // ========== Submit Actions ==========
  const handleConfirmClick = useCallback(() => {
    setConfirmDialogType('submit');
  }, []);

  const cancelConfirm = useCallback(() => {
    setConfirmDialogType(null);
  }, []);

  // 確定ボタン：ローカル保存のみ（APIは呼ばない）
  const confirmLocalSave = useCallback(() => {
    if (!selectedDate) {
      return;
    }

    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    // 編集可能スロットのうち選択されているものが、その日の最終状態
    const selectedKeys = selectedSlots
      .filter(slot => !slot.locked && slot.selected)
      .map(slot => slot.key)
      .sort();

    // サーバー状態と同じ内容なら差分なし → その日のドラフトを取り除く
    // （不可を取り消して元に戻した場合などに、保存ボタンが有効なまま残らないように）
    const serverShift = shifts.find(shift => shift.date === dateStr);
    const dailySlot = (dailySlots ?? []).find(slot => slot.date === dateStr);
    const { slots: baseline } = buildDaySlotSelections(
      monthlyStatusPhase,
      serverShift,
      dailySlot,
    );
    const baselineKeys = baseline
      .filter(slot => !slot.locked && slot.selected)
      .map(slot => slot.key)
      .sort();
    const isSameAsServer =
      selectedKeys.length === baselineKeys.length &&
      selectedKeys.every((key, index) => key === baselineKeys[index]);

    if (isSameAsServer) {
      setPendingChanges(prev => prev.filter(c => c.date !== dateStr));
      closeShiftForm();
      return;
    }

    const timeSlots: TimeSlot[] = selectedSlots
      .filter(slot => !slot.locked && slot.selected)
      .map((slot, index) => ({
        id: index,
        startTime: slot.startTime,
        endTime: slot.endTime,
        slotType: slot.slotType ?? 'normal',
        isConfirmed: false,
        source: slot.source,
      }));

    const myShift: MyShift = {
      id: Date.now(),
      date: dateStr,
      timeSlots,
      status: 'pending',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setPendingChanges(prev => {
      const filtered = prev.filter(c => c.date !== dateStr);
      return [...filtered, { date: dateStr, shift: myShift }];
    });

    closeShiftForm();
  }, [selectedDate, selectedSlots, shifts, dailySlots, monthlyStatusPhase, closeShiftForm]);

  // ========== Save All Changes ==========
  const saveAllChanges = useCallback(async () => {
    if (!doctorId || pendingChanges.length === 0) {
      return;
    }

    if (monthlyStatusPhase !== 'recruiting' && monthlyStatusPhase !== 'fixed_recruiting') {
      setError('現在のステータスではシフトを保存できません。画面を再読み込みしてください。');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const yearMonth = format(currentMonth, 'yyyy-MM');
      const targetDates = pendingChanges.map(change => change.date);
      const shiftInputs: ShiftInput[] = pendingChanges.flatMap(change =>
        change.shift.timeSlots.map(slot => ({
          shift_date: change.date,
          start_time: slot.startTime,
          end_time: slot.endTime,
        }))
      );

      await updateShifts(doctorId, yearMonth, {
        targetDates,
        expectedStatus: monthlyStatusPhase,
        shifts: shiftInputs,
      });

      setPendingChanges([]);
      await fetchData(currentMonth);
    } catch (err: unknown) {
      if (err instanceof MyShiftApiError && err.code === 'STATUS_CONFLICT') {
        // 管理者のフェーズ遷移（締切・確定等）と競合 → 最新状態を再取得。
        // fetchData は冒頭で setError(null) するため、エラー表示は再取得後に設定する
        setPendingChanges([]);
        await fetchData(currentMonth);
        setError(`${err.message}`);
      } else {
        const saveError = err as Error;
        setError(saveError.message || 'シフトの保存に失敗しました。再試行してください。');
      }
    } finally {
      setIsSaving(false);
    }
  }, [doctorId, pendingChanges, currentMonth, monthlyStatusPhase, fetchData]);

  // ========== Retry ==========
  const retry = useCallback(async () => {
    setIsRetrying(true);
    await fetchData(currentMonth, () => setIsRetrying(true));
  }, [currentMonth, fetchData]);

  return {
    // State
    shifts: mergedShifts,
    recruitmentPeriod,
    recruitmentPeriodError,
    currentMonth,
    isLoading,
    error,
    isRetrying,
    dailySlots,
    monthlyStatus,
    monthlyStatusPhase,

    // Pending Changes State
    pendingChanges,
    hasPendingChanges,
    isSaving,

    // Form State
    isFormOpen,
    selectedDate,
    selectedSlots,
    extraShifts,
    formEditable,
    isSubmitting,

    // Confirm Dialog State
    confirmDialogType,

    // Navigation
    handlePreviousMonth,
    handleNextMonth,

    // Form Actions
    openShiftForm,
    closeShiftForm,
    updateSlotSelection,

    // Submit Actions
    handleConfirmClick,
    confirmLocalSave,
    cancelConfirm,

    // Save Actions
    saveAllChanges,

    // Retry
    retry,
  };
};
